import { useEffect, useState } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { Flexbox } from '@/components/ui/flexbox'
import { useAuthStore } from '@/features/auth/store/store.ts'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { ServiceVettingSubmitModal } from '@/features/service-vetting/components/service-vetting-submit-modal'
import { useGetGalleryImages } from '@/features/service-vetting/query/use-get-gallery-images.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import type { RemappedService } from '@/features/service-vetting/types.ts'
import { checkErrors } from '@/features/service-vetting/utils.ts'
import { useTranslation } from '@/translations/i18n'

export const ServiceVettingCriteriaHero = ({
	service
}: {
	service: RemappedService
}) => {
	const { t } = useTranslation()
	const setIsTouched = useServiceVettingError((state) => state.setIsTouched)
	const setErrors = useServiceVettingError((state) => state.setErrors)
	const resetErrors = useServiceVettingError((state) => state.reset)
	const isTouched = useServiceVettingError((state) => state.isTouched)
	const setShowBanner = useServiceVettingError((state) => state.setShowBanner)
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)
	const [isWaitlisted, setIsWaitlisted] = useState(false)
	const imagesQuery = useGetGalleryImages({
		serviceId: service.id,
		galleryId: service.gallery.id
	})
	const user = useAuthStore((state) => state.user)
	const onClickSubmit = () => {
		if (imagesQuery.data) {
			setIsTouched(true)
			const errorCheck = checkErrors({
				service,
				images: imagesQuery.data
			})
			setErrors(errorCheck.errors)
			if (errorCheck.isValid) {
				setShowConfirmationModal(true)
			}
		}
	}
	useEffect(() => {
		if (isTouched && service && imagesQuery.data) {
			const errorCheck = checkErrors({
				service,
				images: imagesQuery.data
			})
			setShowBanner(!errorCheck.isValid)
			setErrors(errorCheck.errors)
		}
	}, [service, isTouched, imagesQuery.data, setShowBanner, setErrors])

	useEffect(() => {
		return () => resetErrors()
	}, [resetErrors])

	const hideAction =
		service.status !== 'under_review' ||
		user?.role === 'read_only' ||
		service.is_host_modified

	return (
		<OpsVettingHero fullWidth justify={'space-between'}>
			<h3>{t('vetting:criteria.service.title')}</h3>
			{!hideAction && (
				<Flexbox align='center' gap={4}>
					<Checkbox
						checked={isWaitlisted}
						id='waitlisted'
						label={t('vetting:criteria.commons.waitlisted')}
						onChange={() => setIsWaitlisted(!isWaitlisted)}
					/>
					<ButtonBrandLegacy
						disabled={imagesQuery.isPending}
						onClick={onClickSubmit}
						size={'sm'}
					>
						{t('common:submit')}
					</ButtonBrandLegacy>
				</Flexbox>
			)}
			{showConfirmationModal && (
				<ServiceVettingSubmitModal
					isWaitlisted={isWaitlisted}
					onClose={() => setShowConfirmationModal(false)}
					service={service}
				/>
			)}
		</OpsVettingHero>
	)
}
